<template>
	<section>
		<!-- Meta tags -->
		<Title>{{ $t("seo.pages.companies.title") }}</Title>
		<Meta name="description" :content="$t('seo.pages.companies.description')" />
		<Link v-if="globalConfig.isBlue" rel="canonical" :href="`https://dreamjobs.hu${$route.fullPath}`" />
		<Link v-else rel="canonical" :href="`https://${globalConfig.host}${$route.fullPath}`" />
		<Meta property="og:url" :content="`https://${globalConfig.host}${$route.fullPath}`" />
		<Meta property="og:type" content="website" />
		<Meta property="og:title" :content="$t('seo.pages.companies.title')" />
		<Meta property="og:description" :content="$t('seo.pages.companies.description')" />
		<Meta
			property="og:locale"
			:content="`${$i18n.locale}_${$i18n.locale == 'en' ? 'US' : $i18n.locale.toUpperCase()}`"
		/>
		<Meta
			property="og:image"
			content="https://res.cloudinary.com/djbs/image/upload/v1669288084/content/DreamJobs/Graphics/ogimage_dreamjobs.png"
		/>
		<Meta property="og:image:type" content="image/jpeg" />
		<Meta property="og:image:width" content="1200" />
		<Meta property="og:image:height" content="628" />
		<Link v-if="globalConfig.isBlue" rel="icon" type="image/x-icon" href="/blue-favicon.ico?v3" />
		<Link v-else rel="icon" type="image/x-icon" href="/favicon.ico?v3" />

		<HeroSubpage
			v-if="companiesList.length > 0"
			type="companies"
			:title="$t('companyhero.title')"
			:subtitle="$t('companyhero.subtitle')"
			:searchData="{ paramValues: searchParamValues, listLength: pagination.total || 0, loading: loading }"
		/>
		<ResponsiveContainer class="flex justify-center mb-10">
			<SearchbarCompanies ref="searchbarCompaniesRef" @setParamValues="setParamValues" />
		</ResponsiveContainer>

		<BannerContainer banner-type="frontend-companies-banner" class="banner-section" v-slot="{ content }">
			<section
				class="container relative flex flex-col md:flex-row justify-center mx-5 sm:mx-8 md:mx-auto my-8"
				v-html="content"
			></section>
		</BannerContainer>
		<AdaptiveContainer v-if="!showFiltered" class="relative flex justify-between md:mt-0 z-10">
			<BaseHeading
				type="h5"
				class="my-9 col-span-full relative before:content-['&nbsp;'] before:block before:absolute before:-bottom-1 before:h-1 before:w-20 before:bg-red-500"
			>
				{{ $t("highlightedCompanies") }}
			</BaseHeading>
			<div class="text-sm hidden sm:flex sm:items-center">
				<!-- anchor tag for reloading, special filter-->
				<a :href="`/${$i18n.locale}/companies?highlightedgold=true`" class="text-sm">
					{{ $t("allCompanies") }}
					<span class="material-icons-outlined pl-2 text-base">arrow_forward</span>
				</a>
			</div>
		</AdaptiveContainer>

		<div v-if="!showFiltered" class="bg-gradient-to-b md:bg-gradient-to-t from-[#F3F7FD]">
			<CarouselCompanies
				ref="carouselCompaniesRef"
				:slideWidth="320"
				:showBanner="!isMobileScreen() && Object.keys(bannerHighlightedCompanies).length > 0"
				:banner="bannerHighlightedCompanies"
			></CarouselCompanies>
		</div>
		<!-- Mobile Banner for Carousel -->
		<AdaptiveContainer
			v-if="!loading && Object.keys(bannerHighlightedCompanies).length > 0 && isMobileScreen() == true"
		>
			<DjCardCompanyBanner
				:subtitle="localized(bannerHighlightedCompanies.sub_title)"
				:title="localized(bannerHighlightedCompanies.title)"
				:description="localized(bannerHighlightedCompanies.content)"
				:cta="localized(bannerHighlightedCompanies.cta_text)"
				:url="localized(bannerHighlightedCompanies.cta_link)"
				:logo="bannerHighlightedCompanies.image.cloudinary"
				class="mt-5 mb-10"
			/>
		</AdaptiveContainer>
		<div class="sm:hidden mb-6">
			<!-- anchor tag for Carousel reloading, special filter-->
			<a
				:href="`/${$i18n.locale}/companies?highlightedgold=true`"
				class="w-full flex justify-center items-center bg-dj-blue/5 text-sm text-dj-blue hover:bg-dj-blue/10 active:bg-dj-blue/10 max-w-[320px] mx-auto mb-4 p-3 rounded-normal"
			>
				{{ $t("allCompanies") }}
				<span class="material-icons-outlined pl-2">arrow_forward</span>
			</a>
		</div>

		<AdaptiveContainer class="my-10" id="company-list">
			<BaseHeading
				v-if="(!loading && showFiltered && companiesList.length > 1) || (!loading && !showFiltered) || loading"
				type="h5"
				class="mt-5 mb-2 col-span-full relative before:content-['&nbsp;'] before:block before:absolute before:-bottom-1 before:h-1 before:w-20 before:bg-red-500"
			>
				<template v-if="!loading && showFiltered && companiesList.length > 1">
					{{ $t("results") }}
				</template>
				<template v-else-if="!loading && !showFiltered">
					{{ $t("furtherCompanies") }}
				</template>
				<template v-else-if="loading">
					<div class="w-[240px] h-6 mb-2 my-auto flex rounded-lg bg-dj-gray-minimal animate-pulse"></div>
				</template>
			</BaseHeading>

			<template v-if="!loading && companiesList.length > 0">
				<template v-for="(company, index) in companiesList" :key="company.id">
					<CardCompany :company="company" newTab />
					<DjCardCompanyBanner
						v-if="Object.keys(bannerOtherCompanies).length > 0 && index == 2 && !showFiltered"
						:subtitle="localized(bannerOtherCompanies.sub_title)"
						:title="localized(bannerOtherCompanies.title)"
						:description="localized(bannerOtherCompanies.content)"
						:cta="localized(bannerOtherCompanies.cta_text)"
						:url="localized(bannerOtherCompanies.cta_link)"
						:logo="bannerOtherCompanies.image.cloudinary"
					/>
				</template>
			</template>

			<!-- Loading state -->
			<template v-else-if="loading">
				<div
					v-for="(card, index) in ['', 'hidden sm:block', 'hidden md:block', 'hidden lg:block']"
					:key="index"
					:class="card"
				>
					<DjCardCompany :loading="true" />
				</div>
			</template>
		</AdaptiveContainer>

		<ResponsiveContainer class="flex">
			<BasePagination
				v-if="companiesList.length != 0 && !loading"
				v-model="paginationPage"
				:total="pagination.last_page || 0"
				class="mx-auto mb-10"
				@change-page="setPage"
				:next-lang="$t('paginationNext')"
				:prev-lang="$t('paginationPrev')"
			/>
		</ResponsiveContainer>

		<ResponsiveContainer v-if="!loading && companiesList.length < 1">
			<PartialNoResult />
		</ResponsiveContainer>
	</section>
</template>

<script lang="ts">
import { ref, defineComponent } from "vue";
import { constructApiUrl } from "@@/composables/helpers";
import { useI18n } from "vue-i18n";

import { type bannerObject } from "@@/types";

export default defineComponent({
	name: "Companies",
	async setup() {
		type CompanyItem = { id: number; [key: string]: unknown };
		type PaginationData = { total?: number; last_page?: number };

		const route = useRoute();
		const router = useRouter();

		const loading = ref(true);

		const bannerOtherCompanies = ref({} as bannerObject);
		const bannerHighlightedCompanies = ref({} as bannerObject);

		const companiesList = ref<CompanyItem[]>([]);
		const pagination = ref<PaginationData>({});
		const searchParamValues = ref("");

		const searchbarCompaniesRef = ref();
		const carouselCompaniesRef = ref<{ isMobile?: () => boolean } | null>(null);

		const paginationPage = ref(1);

		const showFiltered = computed(() => {
			return Object.keys(route.query).length > 0;
		});

		const { locale } = useI18n();

		const langs = useLangs.job;

		const szmdVotingStatus = useSzmdVotingStatus();

		function getQueryPage() {
			const rawPage = route.query.page;
			const pageValue = Array.isArray(rawPage) ? rawPage[0] : rawPage;
			const parsedPage = Number.parseInt(pageValue || "1", 10);
			return Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;
		}

		function localized(value: unknown) {
			const localeMap = value as Record<string, string> | undefined;
			return localeMap?.[locale.value] || "";
		}

		watch(
			() => route.query,
			(newQuery) => {
				if (Object.keys(newQuery).length < 1) {
					searchbarCompaniesRef.value.resetFieldsData();
				}

				paginationPage.value = getQueryPage();

				fetchCompanies().then(() => {
					const scrollTarget = document.getElementById("company-list");
					if (scrollTarget) {
						window.scrollTo({ top: scrollTarget.offsetTop - 76, behavior: "smooth" });
					}
				});
			}
		);

		const isMobileScreen = () => {
			return carouselCompaniesRef.value?.isMobile?.() || false;
		};

		function setPage(page: number) {
			router.push({
				path: `/${locale.value}/companies`,
				query: {
					...route.query,
					page: page > 1 ? page : undefined,
				},
			});
		}

		function buildCompaniesUrl() {
			const url = constructApiUrl("/companies");

			if (route.query.keyword) {
				url.searchParams.append("keyword", route.query.keyword.toString());
			}

			if (route.query.industry) {
				addArrayToParams("tags[industry][]", String(route.query.industry).split(","), url);
			}

			if (route.query.location) {
				addArrayToParams("tags[location][]", String(route.query.location).split(","), url);
			}

			if (route.query.companysize) {
				addArrayToParams("tags[company-size][]", String(route.query.companysize).split(","), url);
			}

			if (route.query.extra) {
				addArrayToParams("badges[]", String(route.query.extra).split(","), url);
			}

			if (route.query.szmd) {
				String(route.query.szmd)
					.split(",")
					.forEach((el) => {
						url.searchParams.append(`szmd[${el.slice(0, 4)}][]`, el.slice(4));
					});
			}

			if (route.query.highlightedgold) {
				url.searchParams.append("scope[]", "highlightedGold");
			}

			if (showFiltered.value == false) {
				url.searchParams.append("scope[]", "notHighlightedGold");
			}

			url.searchParams.append("page", route.query.page ? String(route.query.page) : "1");
			url.searchParams.append(
				"per_page",
				showFiltered.value == true || Object.keys(bannerOtherCompanies.value).length < 1 ? "24" : "23"
			);

			url.searchParams.append("region", useRegion.getCurrentRegion().code.toLowerCase() || "hu");

			return url.href;
		}

		async function fetchCompanies() {
			loading.value = true;
			try {
				const responseData = await $fetch<{ data: CompanyItem[]; pagination: PaginationData }>(
					buildCompaniesUrl()
				);
				companiesList.value = responseData?.data || [];
				pagination.value = responseData?.pagination || {};
			} catch {
				companiesList.value = [];
				pagination.value = {};
			}

			loading.value = false;
		}

		function setParamValues(v: string) {
			searchParamValues.value = v;
		}

		onMounted(() => {
			paginationPage.value = getQueryPage();
			requestBannerOtherCompanies().catch(() => undefined);
			requestBannerHighlightedCompanies().catch(() => undefined);
		});

		async function requestBannerOtherCompanies() {
			bannerOtherCompanies.value = await useBanners.getBanner("other-companies");
		}

		async function requestBannerHighlightedCompanies() {
			bannerHighlightedCompanies.value = await useBanners.getBanner("higlighted-companies");
		}

		const companiesPageCache = useState<{ data: CompanyItem[]; pagination: PaginationData } | null>(
			`companies-page-${route.fullPath}`,
			() => null
		);

		if (!companiesPageCache.value) {
			try {
				companiesPageCache.value = await $fetch<{ data: CompanyItem[]; pagination: PaginationData }>(
					buildCompaniesUrl()
				);
			} catch {
				companiesPageCache.value = null;
			}
		}

		companiesList.value = companiesPageCache.value?.data || [];
		pagination.value = companiesPageCache.value?.pagination || {};
		loading.value = false;

		paginationPage.value = getQueryPage();

		return {
			loading,
			langs,
			locale,
			localized,
			searchbarCompaniesRef,
			carouselCompaniesRef,
			showFiltered,
			isMobileScreen,
			companiesList,
			pagination,
			searchParamValues,
			setParamValues,
			fetchCompanies,
			paginationPage,
			setPage,
			bannerOtherCompanies,
			bannerHighlightedCompanies,
			szmdVotingStatus,
		};
	},
});
</script>
